import type { RouteLocationRaw } from "vue-router";

import type { backendOld } from "~/api";
import { convertToMarkdown, convertToPlainText } from "~/components/text/transformers";
import { isDefined } from "~/shared/common";
import { EntityName, InboxFilter } from "~/shared/enums";
import type { EntityResult, Notification, NotificationUpdate, Page, Status } from "~/shared/types";
import { getCommentLink, getPageDisplayName, getPageLink } from "~/utils/common";
import { notificationComparator } from "~/utils/comparator";
import { getUserDisplayName } from "~/utils/user";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, updateInSorted } from "./utils";

const PAGINATION_KEY = "notifications";
const getPaginationExtra = (filter: InboxFilter) => (filter === InboxFilter.UNREAD ? "unread" : "all");

export type State = {
  _duidsToNotifications: Map<string, Notification>;
  _unreadNotificationCount: number;
  _sortedNotificationDuids: string[];
};

export const state: State = {
  _duidsToNotifications: new Map(),
  _unreadNotificationCount: 0,
  _sortedNotificationDuids: [],
};

export type Getters = {
  unreadNotificationCount: number;
  getNotificationByDuidIfLoaded: (duid: string) => Notification | undefined;
  getLoadedNotificationsOrdered: (filter: InboxFilter) => Notification[];
  _getTenantEntity: () => EntityResult;
  _getUserEntity: (duid: string) => EntityResult | undefined;
  _getUnknownEntity: () => undefined;
  _remapPage: (page: Page | undefined) => EntityResult | undefined;
  getEntity(duid: string, name: EntityName): EntityResult | undefined;
  agetEntity(duid: string, name: EntityName): Promise<EntityResult | undefined>;
  getNotificationEntity: (notification: Notification) => Promise<EntityResult | undefined>;
  getCommentEntityLink: (entityResult: EntityResult) => Promise<RouteLocationRaw | undefined>;
  getEntityLink: (entityResult: EntityResult, entityName: EntityName) => Promise<RouteLocationRaw | undefined>;
};

export type Actions = {
  loadNotifications: (
    ...args: Parameters<typeof backendOld.entityList.notification>
  ) => ReturnType<typeof backendOld.entityList.notification>;
  loadPaginatedNotifications: (filter: InboxFilter) => Promise<number>;
  markAllRead: (options?: { awaitBackend?: boolean }) => Promise<void>;
  updateNotification: (update: NotificationUpdate, options?: { awaitBackend?: boolean }) => Promise<void>;
  updateNotifications: (
    updates: NotificationUpdate[],
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<void>;
  $setUnreadNotificationCount: (count: number) => void;
  $createOrUpdateNotification: (notification: Notification) => void;
  $deleteNotification: (notification: Notification) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  unreadNotificationCount() {
    return Math.max(this._unreadNotificationCount, 0);
  },
  getNotificationByDuidIfLoaded() {
    return (duid) => this._duidsToNotifications.get(duid);
  },
  getLoadedNotificationsOrdered() {
    return (filter: InboxFilter) => {
      const paginationState = this.$getPaginationState(PAGINATION_KEY, getPaginationExtra(filter));

      const placeholderData = this._sortedNotificationDuids;
      const notifications = (paginationState ? [...paginationState.duids.values()] : placeholderData)
        .map((e) => this.getNotificationByDuidIfLoaded(e))
        .filter(isDefined)
        .filter((e) => filter !== InboxFilter.UNREAD || !e.read);
      return paginationState ? notifications.sort(notificationComparator) : notifications;
    };
  },
  _getTenantEntity() {
    return () => {
      const tenant = this.$useTenantStore();

      return {
        title: tenant.name,
        duid: tenant.duid,
      };
    };
  },
  _getUserEntity() {
    return (userDuid) => {
      const user = this.getUserByDuid(userDuid);
      if (!user) {
        return undefined;
      }

      return {
        title: getUserDisplayName(user),
        duid: user.duid,
      };
    };
  },
  _getUnknownEntity() {
    return () => undefined;
  },
  _remapPage() {
    return (page) => {
      if (!page) {
        return undefined;
      }

      return { ...page, title: getPageDisplayName(page, this.getSpaceByDuid) };
    };
  },
  getEntity() {
    return (duid, name) => {
      const ENTITY_NAME_TO_GET_FN_MAP: Record<EntityName, (duid: string) => EntityResult | undefined> = {
        [EntityName.COMMENT]: (e) => {
          const comment = this.getCommentByDuidIfLoaded(e);
          if (!comment) {
            return undefined;
          }

          return {
            duid: comment.duid,
            title: convertToPlainText(convertToMarkdown(comment.text)) ?? "",
          };
        },
        [EntityName.TASK]: this.getTaskByDuidIfLoaded,
        [EntityName.DARTBOARD]: (e) => this._remapPage(this.getDartboardByDuid(e)),
        [EntityName.DASHBOARD]: (e) => this._remapPage(this.getDashboardByDuid(e)),
        [EntityName.VIEW]: (e) => this._remapPage(this.getViewByDuid(e)),
        [EntityName.SPACE]: (e) => this._remapPage(this.getSpaceByDuid(e)),
        [EntityName.DOC]: this.getDocByDuidIfLoaded,
        [EntityName.FOLDER]: (e) => this._remapPage(this.getFolderByDuid(e)),
        [EntityName.FORM]: (e) => this._remapPage(this.getFormByDuid(e)),
        [EntityName.STATUS]: this.getStatusByDuid,
        [EntityName.PROPERTY]: this.getPropertyByDuid,
        [EntityName.TENANT]: this._getTenantEntity,
        [EntityName.USER]: this._getUserEntity,
        // These don't have any FE notifications yet
        [EntityName.UNKNOWN]: this._getUnknownEntity,
      };

      const getFn = ENTITY_NAME_TO_GET_FN_MAP[name];
      return getFn(duid);
    };
  },
  agetEntity() {
    return async (duid, name) => {
      const ENTITY_NAME_TO_GET_FN_MAP: Record<EntityName, (duid: string) => Promise<EntityResult | undefined>> = {
        [EntityName.COMMENT]: async (e) => {
          const comment = await this.getCommentByDuid(e);
          if (!comment) {
            return undefined;
          }

          return {
            duid: comment.duid,
            title: convertToPlainText(convertToMarkdown(comment.text)) ?? "",
          };
        },
        [EntityName.TASK]: this.getTaskByDuid,
        [EntityName.DARTBOARD]: async (e) => this._remapPage(this.getDartboardByDuid(e)),
        [EntityName.DASHBOARD]: async (e) => this._remapPage(this.getDashboardByDuid(e)),
        [EntityName.VIEW]: async (e) => this._remapPage(this.getViewByDuid(e)),
        [EntityName.SPACE]: async (e) => this._remapPage(this.getSpaceByDuid(e)),
        [EntityName.DOC]: this.getDocByDuid,
        [EntityName.FOLDER]: async (e) => this._remapPage(this.getFolderByDuid(e)),
        [EntityName.FORM]: async (e) => this._remapPage(this.getFormByDuid(e)),
        [EntityName.STATUS]: async (e) => this.getStatusByDuid(e),
        [EntityName.PROPERTY]: async (e) => this.getPropertyByDuid(e),
        [EntityName.TENANT]: async () => this._getTenantEntity(),
        [EntityName.USER]: async (e) => this._getUserEntity(e),
        // These don't have any FE notifications yet
        [EntityName.UNKNOWN]: async () => this._getUnknownEntity(),
      };

      const getFn = ENTITY_NAME_TO_GET_FN_MAP[name];
      if (!getFn) {
        throw new Error(`No getFn for entity ${name}`);
      }
      return getFn(duid);
    };
  },
  getNotificationEntity() {
    return async ({ event }) => {
      const { mainEntityName } = event;
      // We still want the task entity for comments
      if (mainEntityName === EntityName.COMMENT) {
        const { taskDuid } = event;
        if (!taskDuid) {
          return undefined;
        }
        return this.agetEntity(taskDuid, EntityName.TASK);
      }

      const duidKey = `${mainEntityName.toLowerCase()}Duid` as keyof typeof event;
      const duid = event[duidKey];
      return this.agetEntity(duid, mainEntityName);
    };
  },
  getCommentEntityLink() {
    return async (entityResult: EntityResult) => {
      const comment = await this.getCommentByDuid(entityResult.duid);
      if (!comment) {
        return undefined;
      }

      const task = await this.getTaskByDuid(comment.taskDuid);
      if (!task) {
        return undefined;
      }

      return getCommentLink(task, comment);
    };
  },
  getEntityLink() {
    return async (entityResult, entityName) => {
      // TODO change this to actually get/have the whole entity so we don't have to cast the entityResult as things that it's definitely not
      if (entityName === EntityName.COMMENT) {
        return this.getCommentEntityLink(entityResult);
      }

      const ENTITY_TO_SETTINGS_LINK_FN_MAP = new Map([
        [EntityName.USER, () => this.$routerUtils.makeLinkToSettingsRef("teammates").value],
        [EntityName.TENANT, () => this.$routerUtils.makeLinkToSettingsRef("workspace").value],
        [EntityName.STATUS, (duid: string) => this.$routerUtils.makeLinkToPropertySettingsRef(duid).value],
        [EntityName.PROPERTY, (duid: string) => this.$routerUtils.makeLinkToPropertySettingsRef(duid).value],
      ]);

      const linkFn = ENTITY_TO_SETTINGS_LINK_FN_MAP.get(entityName);
      if (linkFn !== undefined) {
        const linkDuid = entityName === EntityName.STATUS ? (entityResult as Status).propertyDuid : entityResult.duid;
        return linkFn(linkDuid);
      }

      return getPageLink(entityResult as Page, this.getSpaceByDuid);
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadNotifications(...args) {
    const response = await this.$backendOld.entityList.notification(...args);
    response.data.results.forEach((notification) => {
      if (this.getNotificationByDuidIfLoaded(notification.duid)) {
        return;
      }
      this._duidsToNotifications.set(notification.duid, notification);
      this.$unblacklistEntityDuid(notification.duid);
    });
    return response;
  },
  async loadPaginatedNotifications(filter) {
    const extra = getPaginationExtra(filter);
    let paginationState = this.$getPaginationState(PAGINATION_KEY, extra);

    const { data } = await this.loadNotifications(
      { read: filter === InboxFilter.UNREAD ? false : undefined },
      paginationState?.duids.size ?? 0
    );
    if (!paginationState) {
      paginationState = this.$createPaginationState(PAGINATION_KEY, extra, (a, b) => notificationComparator(b, a));
    }
    this.$addEntitiesToPaginationState(PAGINATION_KEY, extra, data.results, data.count);

    return data.count;
  },
  async markAllRead(options = {}) {
    const { awaitBackend = false } = options;
    if (this.unreadNotificationCount === 0) {
      return;
    }

    this.updateNotifications(
      this.getLoadedNotificationsOrdered(InboxFilter.UNREAD).map((e) => ({ duid: e.duid, read: true })),
      { noBackend: true }
    );
    this.$setUnreadNotificationCount(0);
    this.$clearPaginationState(PAGINATION_KEY, getPaginationExtra(InboxFilter.UNREAD));

    const backendAction = this.$backendOld.notifications.markAllRead();
    if (awaitBackend) {
      await backendAction;
    }
  },
  async updateNotification(update, options) {
    this.updateNotifications([update], options);
  },
  async updateNotifications(updates, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    updates.forEach((update) => {
      const notification = this.getNotificationByDuidIfLoaded(update.duid);
      if (!notification) {
        return;
      }

      // Increment count when read status changes
      if (update.read !== undefined && notification.read !== update.read) {
        this._unreadNotificationCount += update.read ? -1 : 1;

        if (update.read) {
          this.$removeEntityFromPaginationState(PAGINATION_KEY, getPaginationExtra(InboxFilter.UNREAD), notification);
        } else {
          this.$addEntitiesToPaginationState(
            PAGINATION_KEY,
            getPaginationExtra(InboxFilter.UNREAD),
            [notification],
            null
          );
        }
      }

      Object.assign(notification, update);
    });

    if (noBackend) {
      return;
    }

    const backendAction = this.$backend.notification.updateMany(updates);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setUnreadNotificationCount(count) {
    this._unreadNotificationCount = count;
  },
  $createOrUpdateNotification(notification) {
    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (notification.userDuid !== this.$useUserStore().duid) {
      return;
    }

    const currentNotification = this.getNotificationByDuidIfLoaded(notification.duid);

    // Increment count for new unread notification or when read status changes
    if (
      (!currentNotification && !notification.read) ||
      (currentNotification && currentNotification.read !== notification.read)
    ) {
      this._unreadNotificationCount += notification.read ? -1 : 1;
    }

    if (!currentNotification) {
      this._duidsToNotifications.set(notification.duid, notification);
    } else {
      Object.assign(currentNotification, notification);
    }
    updateInSorted(
      this._duidsToNotifications,
      this._sortedNotificationDuids,
      notification.duid,
      notificationComparator
    );

    this.$unblacklistEntityDuid(notification.duid);
    this.$addEntitiesToPaginationStates(PAGINATION_KEY, [notification]);
  },
  $deleteNotification(notification) {
    if (!notification.read) {
      this._unreadNotificationCount -= 1;
    }

    this._duidsToNotifications.delete(notification.duid);
    removeFromSorted(this._sortedNotificationDuids, notification.duid);
    this.$removeEntitiesFromPaginationStates([notification]);
  },
};

export { actions, getters };
